import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '../../shared/ui/button';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { DangerAction } from '../../shared/ui/danger-zone';
import {
  detachCustomerSource,
  sourceClientKeys,
  type TSourceCustomer,
} from '../../entities/customer';

interface IClientRemoveProps {
  sourceId: string;
  row: TSourceCustomer;
  onRemoved: () => void;
}

const recentLine = (row: TSourceCustomer): string => {
  const { activity } = row.customer;
  if (activity.computedAt === null || activity.requestsInWindow === 0) return '';
  const n = activity.requestsInWindow;
  return ` — ${n.toLocaleString('en-US')} request${n === 1 ? '' : 's'} came in on it in the past ${activity.windowHours} h`;
};

export const ClientRemove = ({ sourceId, row, onRemoved }: IClientRemoveProps) => {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const { name, externalId } = row.customer;

  // Soft, as the old dialog was: the address row survives, so adding them back hands back the same URL.
  const remove = useMutation({
    mutationFn: () => detachCustomerSource(row.customer.id, sourceId),
    onSuccess: () => {
      setOpen(false);
      void client.invalidateQueries({ queryKey: sourceClientKeys.all(sourceId) });
      void client.invalidateQueries({ queryKey: ['source', sourceId] });
      onRemoved();
    },
  });

  return (
    <section className="overflow-hidden rounded-panel border border-danger-border">
      <DangerAction
        title="Remove this client"
        action={
          <Button size="sm" variant="secondary" destructive onClick={() => setOpen(true)}>
            Remove…
          </Button>
        }
      >
        Their address stops working at once. Their past runs stay in Events.
      </DangerAction>

      <Modal
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) remove.reset();
        }}
        title={`Remove ${name}?`}
        submit={{
          label: `Remove ${name}`,
          destructive: true,
          onSubmit: () => remove.mutate(),
          waiting: remove.isPending,
          waitingLabel: 'Removing',
        }}
      >
        <div className="flex flex-col gap-3">
          <p className="text-data text-primary">
            Their address stops working at once{recentLine(row)}. Requests after that are refused
            and start no run.
          </p>
          <p className="text-meta text-muted">
            Their past runs stay in Events.
            {externalId ? ` If you add ${name} back, they get this same address.` : ''}
          </p>
          {remove.isError && <Note tone="danger">Not removed: {remove.error.message}</Note>}
        </div>
      </Modal>
    </section>
  );
};
